// libs
import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors'
// constants
import { ERROR_MESSAGES_BY_CODE, ERROR_MESSAGES_BY_KIND } from 'constants/errors'
// types
import { AppError, AppErrorKind } from 'types/errors'
import { AuthErrorCode } from 'types/auth/helpers'
// utils
import { isNetworkError } from 'utils/network'

const INTERNAL_SERVER_ERROR_CODE = 'INTERNAL_SERVER_ERROR'

// Raised by the GraphQL server itself when the request does not match the schema.
// That is a bug in the app, and the message ('Variable "$input" got invalid value…') means nothing to the user.
const MALFORMED_REQUEST_CODES = new Set<string>([
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'OPERATION_RESOLUTION_FAILURE',
  'BAD_REQUEST'
])

type CodedError = { message?: string; extensions?: Record<string, unknown> }

// Variable coercion errors share BAD_USER_INPUT with the validation messages the backend writes for the user,
// so they can only be told apart by the text.
const isMalformedRequestError = ({ message, extensions }: CodedError) => {
  const code = extensions?.code as string

  return (
    MALFORMED_REQUEST_CODES.has(code) || (code === AuthErrorCode.BAD_USER_INPUT && /^Variable "\$/.test(message ?? ''))
  )
}

const AUTH_CODES = new Set<string>([
  AuthErrorCode.UNAUTHENTICATED,
  AuthErrorCode.TOKEN_EXPIRED,
  AuthErrorCode.SESSION_EXPIRED,
  AuthErrorCode.SESSION_REVOKED,
  AuthErrorCode.SESSION_TOKEN_REUSE,
  AuthErrorCode.TWO_FACTOR_REQUIRED
])

const getKindByCodes = (codes: string[]): AppErrorKind => {
  if (codes.some((code) => AUTH_CODES.has(code))) {
    return AppErrorKind.AUTH
  }

  if (codes.includes('FORBIDDEN')) {
    return AppErrorKind.FORBIDDEN
  }

  if (codes.includes(AuthErrorCode.BAD_USER_INPUT)) {
    return AppErrorKind.VALIDATION
  }

  if (codes.includes(INTERNAL_SERVER_ERROR_CODE)) {
    return AppErrorKind.SERVER
  }

  return AppErrorKind.UNKNOWN
}

const getKindByStatus = (statusCode: number): AppErrorKind => {
  if (statusCode === 403) {
    return AppErrorKind.FORBIDDEN
  }

  if (statusCode >= 500) {
    return AppErrorKind.SERVER
  }

  return AppErrorKind.UNKNOWN
}

// Errors that went through the Apollo link chain, i.e. already handled by the ErrorLink
export const isRequestError = (error: unknown) =>
  CombinedGraphQLErrors.is(error) || ServerError.is(error) || ServerParseError.is(error)

export const normalizeError = (error: unknown): AppError => {
  const message = (error as Error)?.message ?? String(error)

  if (CombinedGraphQLErrors.is(error)) {
    const codes = error.errors.map((err) => err.extensions?.code as string).filter(Boolean)
    // A malformed request is not something the user can fix by "checking the entered data"
    const kind = error.errors.every(isMalformedRequestError) ? AppErrorKind.UNKNOWN : getKindByCodes(codes)

    return { kind, codes, message, original: error }
  }

  if (ServerError.is(error)) {
    return { kind: getKindByStatus(error.statusCode), codes: [], message, original: error }
  }

  if (ServerParseError.is(error)) {
    return { kind: AppErrorKind.SERVER, codes: [], message, original: error }
  }

  return {
    kind: isNetworkError(error) ? AppErrorKind.NETWORK : AppErrorKind.UNKNOWN,
    codes: [],
    message,
    original: error
  }
}

export const getUserMessage = ({ kind, codes, original }: AppError): string => {
  const knownCode = codes.find((code) => ERROR_MESSAGES_BY_CODE[code])

  if (knownCode) {
    return ERROR_MESSAGES_BY_CODE[knownCode]
  }

  // Coded backend errors carry a message written for the end user.
  // Unexpected ones (INTERNAL_SERVER_ERROR, a request that does not match the schema) are technical,
  // so they get the generic text below.
  if (CombinedGraphQLErrors.is(original)) {
    const backendMessage = original.errors
      .filter((err) => err.extensions?.code && err.extensions.code !== INTERNAL_SERVER_ERROR_CODE)
      .filter((err) => !isMalformedRequestError(err))
      .map((err) => err.message)
      .filter(Boolean)
      .join(' ')

    if (backendMessage) {
      return backendMessage
    }
  }

  return ERROR_MESSAGES_BY_KIND[kind]
}
